import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AgreementResource, Assignment, AssignmentResource, Contact, ContactResource, MessagingSettings, NotificationResource, SettingsResource, Shift } from '@apaq/orgkit-js';
import { PageRequest } from "@apaq/leap-data-core";
import { differenceInMinutes, isBefore, isToday, set } from 'date-fns';
import { container } from 'tsyringe';
import log from 'loglevel';
import { LinkService } from 'src/app/services/link.service';
import { AgreementSummary } from '../../../assignment-preview-dialog/assignment-preview-dialog.component';
import { NotificationHelper } from 'src/app/common/notification-helper';
import { AssignmentHelper } from 'src/app/common/assignment-helper';
import { GeoJsonObject } from 'geojson';

export interface DataSource {
  /**
   * The duration of the current route given as seconds.
   */
  get duration(): number;

  /**
   * The locations of the current route including start and end. Each location is given as Geojson formatted location (x, y).
   */
  get locations(): number[][];

  /**
   * The current route as geojson object.
   */
  get route(): GeoJsonObject | undefined;

  /**
   * Allows for specifying a callback method that must be called everytime the route changes.
   * @param callback Callback for when route changes.
   */
  onRouteChanged(callback: () => void): void;
}

@Component({
  selector: 'ws-work-plan-map',
  templateUrl: './work-plan-map.component.html',
  styleUrls: ['./work-plan-map.component.scss']
})
export class WorkPlanMapComponent {

  @ViewChild('map') map?: ElementRef<SlMapComponent>;
  @ViewChild('popover') popover?: ElementRef;

  @Input() currentDate?: Date = new Date();
  @Output() valueChange = new EventEmitter<Assignment>();
  @Output() editAssignment = new EventEmitter();
  @Output() deleteAssignment = new EventEmitter();
  @Output() lockAssignment = new EventEmitter();
  @Output() unlockAssignment = new EventEmitter();

  agreementSummaries?: AgreementSummary[];
  contact?: Contact;
  messagingSettings?: MessagingSettings;
  _assignments?: Assignment[];
  _value?: Assignment;
  _datasource?: DataSource;
  _bubbleList: HTMLElement[] = [];

  constructor(private linkService: LinkService,
    @Inject(LOCALE_ID) private locale: string,
    private assignmentResource: AssignmentResource,
    private agreementResource: AgreementResource,
    private notificationResource: NotificationResource,
    private settingsResource: SettingsResource,
    private contactResource: ContactResource) {

    this.handleBubbleClick = this.handleBubbleClick.bind(this);
    this.handleBubbleTransitionEnd = this.handleBubbleTransitionEnd.bind(this);
    this.settingsResource.findById('messaging').then(settings => this.messagingSettings = settings as MessagingSettings)
  }

  onEditAssignment() {
    this.editAssignment.emit();
  }

  onStartAssignment() {
    Stallion.confirm($localize`Do you want to mark the assignment as started now? The start time of the assignment will be changed to now and duration will be tracked.`).then(result => {
      if (result === true) {
        this.assignmentResource.save({
          id: this.value?.id,
          plan: {
            executionTime: new Date(),
            allDay: false
          },
          action: 'InProgress'
        } as Assignment);
      }
    });
  }

  async onCompleteAssignment() {
    await this.doMarkAction($localize`Do you want to mark the assignment as completed?`, 'Completed');
    this.value = null!;
  }

  async onSkipAssignment() {
    await this.doMarkAction($localize`Do you want to mark the assignment as skipped?`, 'Skipped');
    this.value = null!;
  }

  onOpenContact() {
    log.debug('Opening Contact: ', this.value?.contactId);
    this.linkService.openContact(this.value!.contactId);
    this.value = null!;
  }

  onOpenReference(reference: string) {
    log.debug('Opening reference:', reference);
    this.value = null!;
    this.linkService.openAgreement(reference);
  }

  onLock() {
    this.lockAssignment.emit();
  }

  onUnlock() {
    this.unlockAssignment.emit();
  }

  async onSendNotification() {
    log.debug('Sending notification for assignment.', this.value);
    await this.notificationResource.generateImpendingAssignmentNotification([this.value!.id]);
    this.value = null!;
  }

  onPopoverHide(event: CustomEvent) {
    const popoverId = (event.target as HTMLElement).dataset['assignmentId'];
    if (this.value != null && this.value.id === popoverId) {
      event.preventDefault();
    }
  }

  onMapMove(_: CustomEvent) {
    this.popover?.nativeElement.update();
  }

  get introFile() {
    return `https://assets.previsto.com/videos/${this.locale}/walkthroughs/assignment-overview.mp4`;
  }

  get assignments() {
    return this._assignments!;
  }

  @Input()
  set assignments(value: Assignment[] | undefined) {
    this._assignments = value;
  }

  get autoNotificationEnabled() {
    const possible = NotificationHelper.isNotificationPossible(this.contact, this.messagingSettings);
    if (!possible) return false;

    return NotificationHelper.isNotificationWantedForEvent('assignment.impending', this.contact, this.messagingSettings);
  }

  get allowManualNotification() {
    return NotificationHelper.isNotificationPossible(this.contact, this.messagingSettings);
  }

  get value() {
    return this._value!;
  }

  @Input()
  set value(value: Assignment | undefined) {
    this.handleValueChange(value);
  }

  get initialLng() {
    return this.initialLocation[0];
  }

  get initialLat() {
    return this.initialLocation[1];
  }

  get initialLocation() {
    const locations: number[][] = this._datasource?.locations ?? [];
    const location = locations.length > 0 ? locations[0] : [0.0, 0.0];
    return location;
  }

  get datasource() {
    return this._datasource!;
  }

  @Input()
  set datasource(value: DataSource) {
    this._datasource = value;
    if (this._datasource != null) {
      this.datasource.onRouteChanged(() => {
        this.loadRoute();
        this.addMarkers();
      });
    }
  }

  private async handleValueChange(value: Assignment | undefined) {
    const valueChanged = value != this._value;
    if (this.value != null) {
      const bubble = this.getBubble(this.value.id);
      if (bubble != null) {
        bubble.style.fontSize = 'inherit';
        bubble.style.zIndex = 'inherit';
        bubble.dataset['open'] = 'false';
        await this.hidePopup();
      }
    }

    if (value != null) {
      const bubble = this.getBubble(value.id);
      if (bubble != null) {
        bubble.style.fontSize = '2rem';
        bubble.style.zIndex = '1';
        bubble.dataset['open'] = 'true';
      }
    }

    if (valueChanged) {
      this.contact = value == null ? undefined : await this.contactResource.findById(value.contactId);
      this._value = value;
      this.valueChange.emit(value);
      this.updateAgreementSummaries();

      if (value != null) {
        this.map!.nativeElement.locate(value.location[0], value.location[1]);
      }
    }
  }

  private async updateAgreementSummaries() {
    this.agreementSummaries = [];

    if (this.value != null) {
      const references = this.value.tasks.filter(t => t.reference != null).map(t => t.reference);
      const p1 = this.agreementResource.findAll({ query: { id: references } } as PageRequest);
      const p2 = references.length > 0 ? this.assignmentResource.getPrevious({ agreementId: references, referenceDate: this.value.plan.executionTime, action: 'Completed' }) : Promise.resolve([]);
      const data = (await Promise.all([p1, p2]));
      const agreements = data[0].content;
      const assignments = data[1];

      agreements.forEach(a => {
        let prev: Date;
        assignments.forEach(asgmt => {
          const task = asgmt.tasks.find(t => t.reference === a.id);
          if (task != null && (prev == null || isBefore(prev, asgmt.plan.executionTime))) {
            prev = asgmt.plan.executionTime;
          }
        });
        this.agreementSummaries?.push({ id: a.id, recurrenceRule: a.recurrenceRule, previousExecution: prev! });
      });
    }
  }

  private showPopup(value: Assignment) {
    const bubble = this.getBubble(value.id);
    const popoverEl = this.popover?.nativeElement as any;

    popoverEl.anchorElement = bubble;
    popoverEl.dataset.assignmentId = value.id;
    popoverEl.show();
  }

  hidePopup(): Promise<void> {
    const popoverEl = this.popover?.nativeElement as any;
    delete popoverEl.dataset.assignmentId;
    return popoverEl.hide();
  }

  private getBubble(assignmentId: string): HTMLElement {
    return this.map?.nativeElement.shadowRoot?.querySelector(`sl-bubble[data-assignment-id=${assignmentId}]`) as HTMLElement;
  }

  private async loadRoute() {
    log.info('Loading route');
    if (this._assignments == null || this._assignments.length === 0 || this.datasource == null) {
      await this.map?.nativeElement.clearSources();
      return;
    }


    await this.map?.nativeElement.clearSources();
    this.map?.nativeElement.addSource('route', 'geojson', this.datasource.route);

  }

  private async doMarkAction(confirmText: string, action: 'Completed' | 'Skipped' | 'InProgress'): Promise<void> {
    if (!this.value || !await Stallion.confirm(confirmText)) return;

    const assignment = this.value;
    const now = new Date();

    // TODO: Test that execution time is only changed if action is InProgress

    let finalAssignment: Assignment;
    let timestamp: Date | undefined;
    if (assignment.action === 'InProgress' && action === 'Completed' && isToday(assignment.plan.executionTime)) {
      const minutesSincesStart = differenceInMinutes(new Date(), assignment.plan.executionTime);
      AssignmentHelper.divideMinutesAmongTasks(assignment, minutesSincesStart);
      finalAssignment = await this.assignmentResource.save(assignment);
      timestamp = set(this.currentDate!, { hours: now.getHours(), minutes: now.getMinutes(), seconds: 0 });
    } else if(assignment.plan.allDay) {
      if(isToday(this.currentDate!)) {
        timestamp = set(this.currentDate!, { hours: now.getHours(), minutes: now.getMinutes(), seconds: 0 });
      } else {
        timestamp = set(this.currentDate!, { hours: 12, minutes: 0, seconds: 0 });
      }
      finalAssignment = assignment;
    } else {
      finalAssignment = assignment;
    }

    this.assignmentResource.markHandle({
      accountId: finalAssignment.accountId,
      timestamp: timestamp as Date,  // <!-- timestamp has incorrect model TODO
      affectSeries: true, // <- This is not really needed anymore
      entries: [
        {
          action,
          assignmentId: finalAssignment.id
        }
      ]
    })

  }

  private addMarkers() {
    log.info('Adding markers');
    this._bubbleList.forEach(bubble => {
      bubble.removeEventListener('click', this.handleBubbleClick);
      bubble.removeEventListener('transitionend', this.handleBubbleTransitionEnd);
    });

    this.map?.nativeElement.clearMarkers().then(() => {

      if (this.datasource != null) {
        const locs = this.datasource.locations;
        const id = new Date().toUTCString();
        this.map?.nativeElement.addMarker(id + '-start',
          locs[0][0],
          locs[0][1],
          { element: this.createHomeBubble(), anchor: 'bottom' });

        this.map?.nativeElement.addMarker(id + '-end',
          locs[locs.length - 1][0],
          locs[locs.length - 1][1],
          { element: this.createHomeBubble(), anchor: 'bottom' });
      }

      let index = 0;
      this._assignments?.forEach((a) => {
        let bubble: HTMLElement;
        if (a.action === 'Completed') {
          bubble = this.createCompletedBubble();
          index++;
        } else if (a.action === 'Skipped') {
          bubble = this.createSkippedBubble();
        } else if (a.plan.allDay) {
          bubble = this.createLongTermBubble();
        } else {
          bubble = this.createBubble();
          bubble.innerHTML = `${++index}`;
        }
        this._bubbleList.push(bubble);
        bubble.setAttribute('data-assignment-id', a.id);
        bubble.addEventListener('click', this.handleBubbleClick);
        bubble.addEventListener('transitionend', this.handleBubbleTransitionEnd);
        this.map?.nativeElement.addMarker(a.id, a.location[0], a.location[1], { element: bubble, anchor: 'bottom' });
      });
      this.map?.nativeElement.fitBounds();
    });
  }

  private handleBubbleClick(event: MouseEvent) {
    const bubble = event.target as HTMLElement;
    const id = bubble.getAttribute('data-assignment-id');
    const assignment = this._assignments?.find(a => a.id === id);

    bubble.focus();
    this.value = assignment;
  }

  private async handleBubbleTransitionEnd(event: Event) {
    const bubble = event.target as HTMLElement;
    if (bubble.dataset['open'] === 'true') {
      await this.hidePopup();
      const id = bubble.getAttribute('data-assignment-id');
      const assignment = this._assignments?.find(a => a.id === id);
      this._value = assignment;

      if (assignment) {
        this.showPopup(assignment);
      }
    }
  }

  private createHomeBubble() {
    const bubble = this.createBubble();
    const homeIcon = document.createElement('sl-icon');
    homeIcon.setAttribute('name', 'house');
    bubble.appendChild(homeIcon);
    return bubble;
  }

  private createLongTermBubble() {
    const bubble = this.createBubble();
    const icon = document.createElement('sl-icon');
    icon.setAttribute('name', 'circle');
    bubble.appendChild(icon);
    return bubble;
  }

  private createSkippedBubble() {
    const bubble = this.createBubble();
    const icon = document.createElement('sl-icon');
    icon.setAttribute('name', 'dash-circle-dotted');
    bubble.appendChild(icon);
    return bubble;
  }

  private createCompletedBubble() {
    const bubble = this.createBubble();
    const icon = document.createElement('sl-icon');
    icon.setAttribute('name', 'check2');
    bubble.appendChild(icon);
    return bubble;
  }

  private createBubble() {
    const bubble = document.createElement('sl-bubble');
    bubble.style.transition = 'font-size var(--sl-transition-fast)';
    bubble.style.filter = 'drop-shadow(0px 1px 2px #6b7280)';
    return bubble;
  }
}
